import React, { ReactNode, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CloudIcon } from "@heroicons/react/24/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { InstanceAttribute } from "components/Environment/InstanceAttribute";
import {
  displayPlatformType,
  formatMemory,
  getCloudPlatformUrl,
} from "components/Environment/utils";
import {
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import CloudDisplay from "components/Graphs/CloudDisplay";
import { isDgxSpark, isDgxStation } from "utils/instanceTypes";
import { getHardwareImage } from "utils/brevcloud";
import Chip from "components/Environment/Create/chips";
import { classNames, memoryToGiB } from "components/utils";
import { InfoTooltip } from "components/Graphs/InfoTooltip";
import { getDefaultStorageGiB } from "utils/instanceTypes";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
import { getInstanceTypeArchitectures } from "utils/architecture";

interface ComputeOverviewProps {
  instanceTypeObject: GPUInstanceType;
  diskStorage: string | null;
  intendedDiskStorage?: string | null;
  warningSection?: ReactNode;
  showCloudProvider?: boolean;
  showInstanceTypeLabel?: boolean;
  cloudProviderContent?: ReactNode;
  onViewAllOptions?: () => void;
  onStorageChange?: (storage: string) => void;
  onStorageValidityChange?: (isValid: boolean) => void;
}

export const ComputeOverview: React.FC<ComputeOverviewProps> = ({
  instanceTypeObject,
  diskStorage,
  intendedDiskStorage,
  warningSection,
  showCloudProvider = true,
  showInstanceTypeLabel = true,
  cloudProviderContent,
  onViewAllOptions,
  onStorageChange,
  onStorageValidityChange,
}) => {
  const providerName = GetProviderName(instanceTypeObject) || "";
  const cloudCred = getPrimaryInstanceCloudCred(instanceTypeObject);

  const storageMin = (() => {
    const mins = (instanceTypeObject.supported_storage || [])
      .filter((s) => s.min_size)
      .map((s) => memoryToGiB(s.min_size!));
    return mins.length > 0 ? Math.min(...mins) : null;
  })();
  const storageMinLabel =
    storageMin !== null ? `${Math.ceil(storageMin)}GiB` : undefined;

  const storageMax = (() => {
    const maxs = (instanceTypeObject.supported_storage || [])
      .filter((s) => s.max_size)
      .map((s) => memoryToGiB(s.max_size!));
    return maxs.length > 0 ? Math.max(...maxs) : null;
  })();
  const storageMaxLabel =
    storageMax !== null ? `${Math.floor(storageMax)}GiB` : undefined;

  const getEffectiveStorage = (storage: string | null) => {
    if (storage) return storage;
    if (instanceTypeObject.elastic_root_volume) {
      const defaultGiB = Number(
        getDefaultStorageGiB(instanceTypeObject.supported_storage),
      );
      return String(
        storageMin !== null ? Math.max(defaultGiB, storageMin) : defaultGiB,
      );
    }
    return "";
  };

  const [inputValue, setInputValue] = useState<string>(
    getEffectiveStorage(diskStorage),
  );

  const isStorageValid = (value: string): boolean => {
    if (!instanceTypeObject.elastic_root_volume) return true;
    const num = Number(value);
    if (!value || isNaN(num) || num <= 0) return storageMin === null;
    if (storageMin !== null && num < storageMin) return false;
    if (storageMax !== null && num > storageMax) return false;
    return true;
  };

  useEffect(() => {
    const effective = getEffectiveStorage(diskStorage);
    setInputValue(effective);
    onStorageValidityChange?.(isStorageValid(effective));
    if (!diskStorage && effective) {
      onStorageChange?.(effective);
    }
  }, [diskStorage, instanceTypeObject]); // eslint-disable-line react-hooks/exhaustive-deps

  const storageErrorMessage = (() => {
    if (!instanceTypeObject.elastic_root_volume) return null;
    const num = Number(inputValue);
    const isEmpty = !inputValue || isNaN(num) || num <= 0;
    if (isEmpty) {
      if (storageMin !== null && storageMax !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      if (storageMin !== null) {
        return `Value must be greater than ${storageMinLabel}`;
      }
      return null;
    }
    if (storageMin !== null && num < storageMin) {
      if (storageMax !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      return `Value must be greater than ${storageMinLabel}`;
    }
    if (storageMax !== null && num > storageMax) {
      if (storageMin !== null) {
        return `Value must be between ${storageMinLabel}–${storageMaxLabel}`;
      }
      return `Value must be less than ${storageMaxLabel}`;
    }
    return null;
  })();

  const handleStorageChange = (value: string) => {
    setInputValue(value);
    const valid = isStorageValid(value);
    onStorageValidityChange?.(valid);
    if (valid) {
      onStorageChange?.(value);
    }
  };

  const isKnownHardware =
    isDgxSpark(instanceTypeObject.type) ||
    isDgxStation(instanceTypeObject.type);

  const gpu = instanceTypeObject.supported_gpus?.[0];
  const isGPUInstance = !!gpu;

  return (
    <div className="flex flex-col gap-4">
      <div>
        {(showInstanceTypeLabel || onViewAllOptions) && (
          <div className="flex flex-row justify-between items-center mb-1">
            {showInstanceTypeLabel && (
              <p className="text-sm text-secondary font-medium">Instance type</p>
            )}
            {onViewAllOptions && (
              <span
                className="flex items-center gap-1 text-sm font-bold text-highlight cursor-pointer"
                onClick={onViewAllOptions}
              >
                <PencilIcon className="w-3 h-3" />
                Edit
              </span>
            )}
          </div>
        )}
        <div className="border border-zinc-800 rounded-md bg-zinc-900/30 text-primary">
          <div>
            <div className="flex flex-row items-center">
              <AnimatePresence mode="wait">
                {isGPUInstance ? (
                  isKnownHardware ? (
                    <motion.div
                      key="hardware-image"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="relative overflow-hidden bg-black flex-shrink-0 self-stretch rounded-l-md"
                      style={{
                        width: "25%",
                        backgroundImage: `url(${getHardwareImage(
                          isDgxSpark(instanceTypeObject.type)
                            ? "dgx-spark"
                            : instanceTypeObject.provider || "",
                        )})`,
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  ) : (
                    <motion.div
                      key="chip-image"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex relative bg-black flex-shrink-0 self-stretch rounded-l-md items-center justify-center px-2"
                      style={{
                        width: "25%",
                      }}
                    >
                      <Chip
                        selected={true}
                        chipName={gpu.name || ""}
                        manufacturerName={gpu.manufacturer || ""}
                        onClick={() => {}}
                        className={classNames("h-[68px] flex-shrink-0")}
                      />
                    </motion.div>
                  )
                ) : (
                  <motion.div
                    key="cpu-chip"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={classNames(
                      "flex relative bg-black flex-shrink-0 self-stretch rounded-l-md items-center justify-center px-2",
                      isGPUInstance
                        ? "w-[25%] min-h-[80px]"
                        : "h-[70px] w-[70px]",
                    )}
                  >
                    <span
                      className="font-bold text-lg select-none bg-clip-text text-transparent"
                      style={{
                        backgroundImage:
                          "linear-gradient(to right, #FEFFFF, #888C93)",
                      }}
                    >
                      CPU
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
              <div
                className={classNames(
                  "px-3 py-2 flex-1",
                  isGPUInstance ? "" : "flex flex-col gap-1",
                )}
              >
                <InstanceAttribute
                  instanceType={instanceTypeObject.type}
                  isGPUInstance={isGPUInstance}
                  vcpu={instanceTypeObject.vcpu}
                  memory={formatMemory(
                    instanceTypeObject.memory,
                    cloudCred?.platformType || "cloud",
                  )}
                  gpuManufacturer={gpu?.manufacturer || ""}
                  gpuName={gpu?.name || ""}
                  gpuMemory={gpu?.memory || ""}
                  gpuCount={gpu?.count || 0}
                  architectures={getInstanceTypeArchitectures(
                    instanceTypeObject,
                  )}
                  plainArchitecture
                />
                {showCloudProvider && !cloudProviderContent && (
                    <div className="flex flex-col text-secondary">
                      <div className="flex flex-row mb-1 items-center">
                        <CloudDisplay
                          provider={displayPlatformType(
                            providerName || "Unknown",
                          )}
                          icon={<CloudIcon className="h-4 w-4" />}
                          tooltip={`Open ${getCloudPlatformUrl(
                            providerName,
                          )} in a new tab`}
                          onClick={() => {
                            window.open(
                              getCloudPlatformUrl(providerName),
                              "_blank",
                            );
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>
            {warningSection}
            {cloudProviderContent && (
              <div className="border-t border-zinc-800 p-3">
                {cloudProviderContent}
              </div>
            )}
          </div>
        </div>
      </div>
      {onStorageChange && (
        <div>
          <div className="flex flex-row justify-between items-center mb-1">
            <div className="flex items-center">
              <p className="text-sm text-secondary font-medium">Storage</p>
              <InfoTooltip
                text={
                  instanceTypeObject.elastic_root_volume
                    ? "Storage is billed based on the amount provisioned and continues to accrue even when your instance is stopped."
                    : "The storage amount for this instance is fixed by the cloud provider and cannot be changed."
                }
              />
            </div>
            {instanceTypeObject.elastic_root_volume ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  placeholder="256"
                  value={inputValue}
                  onChange={(e) => handleStorageChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.metaKey ||
                      e.ctrlKey ||
                      /[0-9\b]/.test(e.key) ||
                      e.key === "Backspace" ||
                      e.key === "Delete" ||
                      e.key === "Tab" ||
                      e.key === "ArrowLeft" ||
                      e.key === "ArrowRight"
                    ) {
                      return;
                    }
                    e.preventDefault();
                  }}
                  className="w-24 px-2 py-1 text-sm rounded border border-zinc-700 bg-zinc-800 text-primary focus:outline-none focus:ring-1 focus:ring-highlight [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-sm text-secondary">GiB</span>
              </div>
            ) : (
              <span className="text-sm text-secondary">
                {instanceTypeObject.supported_storage?.find((s) => s.size)
                  ?.size || "Fixed by provider"}
              </span>
            )}
          </div>
          {storageErrorMessage && (
            <p className="text-xs text-red-500 mt-1 text-right">
              {storageErrorMessage}
            </p>
          )}
          {instanceTypeObject.elastic_root_volume &&
            intendedDiskStorage &&
            isStorageValid(inputValue) &&
            Number(inputValue) < Number(intendedDiskStorage) && (
              <p className="text-xs text-yellow-500 mt-2 text-center">
                The original configuration uses {intendedDiskStorage} GiB.
                Reducing storage may cause the launchable to run out of disk
                space or behave unexpectedly.
              </p>
            )}
        </div>
      )}
    </div>
  );
};
